import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tooltip } from "@air/primitive-tooltip";
import { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { CustomFieldListWithOverflow } from "~/components/CustomFields/CustomFieldListWithOverflow";
import { GalleryItemType } from "~/components/Gallery/types";
import { GalleryBoardCustomFieldAction } from "~/components/v2/components/shared/gallery/GalleryBoardCustomFieldAction";
import { GalleryBoardMenu } from "~/components/v2/components/shared/gallery/GalleryBoardMenu";
import { GalleryBoardThumbnail } from "~/components/v2/components/shared/gallery/GalleryBoardThumbnail";
import { GallerySelectableCard } from "~/components/v2/components/shared/gallery/GallerySelectableCard";
import { currentCardSizeSelector, visibleCustomFieldsViewOptionsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
const _GalleryBoard = (param)=>{
    let { ancestors, box, board, canViewCustomFields, getItemMenuOptions, getSelectionMenuOptions, libraryCustomFields, isSelectable, onClick, onCmdClickTitle } = param;
    const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
    const configViewCardSize = useSelector(currentCardSizeSelector);
    const filteredLibraryCustomFields = useMemo(()=>{
        if (libraryCustomFields === null || libraryCustomFields === void 0 ? void 0 : libraryCustomFields.length) {
            var _board_customFields;
            return board === null || board === void 0 ? void 0 : (_board_customFields = board.customFields) === null || _board_customFields === void 0 ? void 0 : _board_customFields.filter((boardField)=>{
                var _libraryCustomFields_find;
                return !!((_libraryCustomFields_find = libraryCustomFields.find((customField)=>customField.id === boardField.id)) === null || _libraryCustomFields_find === void 0 ? void 0 : _libraryCustomFields_find.visible);
            });
        }
        return board.customFields;
    }, [
        board === null || board === void 0 ? void 0 : board.customFields,
        libraryCustomFields
    ]);
    const filteredVisibleCustomFields = useMemo(()=>{
        return filteredLibraryCustomFields === null || filteredLibraryCustomFields === void 0 ? void 0 : filteredLibraryCustomFields.filter((customField)=>visibleCustomFields.find((vcf)=>vcf.customFieldId === customField.id));
    }, [
        filteredLibraryCustomFields,
        visibleCustomFields
    ]);
    const item = useMemo(()=>({
            id: board.id,
            item: board,
            type: GalleryItemType.board
        }), [
        board
    ]);
    const onTitleClick = useCallback((event)=>{
        if ((event.metaKey || event.ctrlKey) && !!onCmdClickTitle) {
            onCmdClickTitle();
        } else {
            onClick();
        }
    }, [
        onClick,
        onCmdClickTitle
    ]);
    return /*#__PURE__*/ _jsx(GallerySelectableCard, {
        className: "h-full rounded-12 bg-grey-1 p-1 ring-1 ring-inset ring-grey-5 ring-offset-0 transition-colors data-[hovering=true]:bg-grey-4 data-[selected=true]:bg-grey-4 data-[highlighted=true]:ring-2 data-[selected=true]:ring-2 data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-blue-9",
        getItemMenuOptions: getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        item: item,
        isSelectable: isSelectable,
        onClick: onClick,
        children: (param)=>{
            let { isHovering, setIsMenuOpen } = param;
            return /*#__PURE__*/ _jsxs("div", {
                className: "size-full",
                children: [
                    /*#__PURE__*/ _jsx(GalleryBoardThumbnail, {
                        board: board,
                        sizes: "100vw"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none absolute inset-x-1 top-1 flex items-center justify-between p-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-auto",
                                children: !!(filteredLibraryCustomFields === null || filteredLibraryCustomFields === void 0 ? void 0 : filteredLibraryCustomFields.length) && canViewCustomFields && /*#__PURE__*/ _jsx(GalleryBoardCustomFieldAction, {
                                    customFields: filteredLibraryCustomFields
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-auto",
                                children: isHovering && /*#__PURE__*/ _jsx(GalleryBoardMenu, {
                                    board: board,
                                    getItemMenuOptions: getItemMenuOptions,
                                    getSelectionMenuOptions: getSelectionMenuOptions,
                                    setIsMenuOpen: setIsMenuOpen
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none absolute inset-x-1 bottom-1 flex flex-col items-start gap-0.5 rounded-b-8 bg-gradient-to-t from-black/40 to-transparent px-3 py-2",
                        style: {
                            textShadow: "0px 0px 4px rgba(0, 0, 0, 0.40)"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Tooltip, {
                                align: "start",
                                label: board.title,
                                side: "top",
                                children: /*#__PURE__*/ _jsx("button", {
                                    className: "pointer-events-auto text-left text-18 font-semibold leading-[24px] text-white hover:underline",
                                    "data-testid": "GALLERY_BOARD_TITLE",
                                    onClick: onTitleClick,
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: configViewCardSize === "large" || configViewCardSize === "extra-large" ? "line-clamp-3" : "line-clamp-2",
                                        children: board.title
                                    })
                                })
                            }),
                            !!ancestors && /*#__PURE__*/ _jsxs("span", {
                                className: "text-12 leading-[18px] text-white",
                                children: [
                                    "in ",
                                    ancestors
                                ]
                            }),
                            !!(filteredVisibleCustomFields === null || filteredVisibleCustomFields === void 0 ? void 0 : filteredVisibleCustomFields.length) && canViewCustomFields && /*#__PURE__*/ _jsx(CustomFieldListWithOverflow, {
                                allowedWidth: box.width,
                                customFields: filteredVisibleCustomFields,
                                hasDarkBackground: true,
                                shouldShowTooltip: true,
                                size: "small"
                            })
                        ]
                    })
                ]
            });
        }
    });
};
export const GalleryBoard = /*#__PURE__*/ memo(_GalleryBoard);
